/** The bell, in the bar: the way to Action items (ADR-0212 decision 6c), with the number of things
 * waiting on the signed-in person.
 *
 * **The count is the one Home and the first tab of Action items print** (`useWaitingOnMe`, one
 * query): approvals that name the person, tasks they own, bills to review. It used to be the unread
 * notification count; notifications are a tab of Action items now and carry their own unread tile, so
 * the bell no longer answers a different question from the page it opens.
 *
 * Three states, and none may be mistaken for another (EXP-29): still reading draws no number,
 * because nothing has been said yet; a failed read draws `?` in the warning colour and says in its
 * name that the count could not be read - **never a zero**, which would tell a person with a review
 * waiting that nothing is; a count of nothing draws no badge and is still said in the name.
 */

import { Bell } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";

import { useWaitingOnMe } from "../api/waiting";
import type { Scope2Method } from "../api/queries";
import type { Window } from "../screens/useScreen";
import styles from "./AppShell.module.css";

type BellRead =
  | { state: "none" }
  | { state: "unread" }
  | { state: "counted"; count: number };

/** The number the bell shows: what is waiting on the person, from the read Action items uses. */
export function useBellCount(): BellRead {
  const { data, error } = useWaitingOnMe();
  if (error) return { state: "unread" };
  if (data === undefined) return { state: "none" };
  return { state: "counted", count: data.total };
}

export function ActionBell({
  method,
  period,
}: {
  method: Scope2Method;
  period: Window | undefined;
}) {
  const read = useBellCount();
  const counted = read.state === "counted" ? read.count : undefined;
  const shown = counted !== undefined ? (counted > 99 ? "99+" : String(counted)) : "?";
  const name =
    read.state === "unread"
      ? "Action items, what is waiting on you could not be read"
      : counted !== undefined && counted > 0
        ? `Action items: ${String(counted)} waiting on you`
        : "Action items";
  return (
    <Link
      to="/approvals"
      search={(prev: Record<string, unknown>) => ({
        ...prev,
        method,
        from: period?.period_start,
        to: period?.period_end,
        tab: undefined,
      })}
      className={styles.iconButton}
      data-testid="bell"
      aria-label={name}
      title={name}
    >
      <Bell size={16} aria-hidden="true" />
      {read.state === "unread" || (counted !== undefined && counted > 0) ? (
        <span
          className={styles.bellBadge}
          data-testid="bell-badge"
          data-state={read.state}
          data-count={counted !== undefined ? String(counted) : undefined}
          aria-hidden="true"
        >
          {shown}
        </span>
      ) : null}
    </Link>
  );
}
